import { useIsApiReady } from "@/hooks/use-api-ready";
import {
  fileControllerGetFile,
  fileControllerGetMarkdown,
  fileControllerGetParsingProgress,
  fileControllerGetRagSummary,
  fileControllerParseFile,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

const POLLING_INTERVAL = 2000;

export const useFileData = (params: { fileUuid: string | null }) => {
  const { fileUuid } = params;
  const ready = useIsApiReady() && !!fileUuid;

  const { data: file, refetch } = useQuery({
    enabled: ready,
    queryKey: queryKeys.file({
      fileUuid: fileUuid!,
    }),
    queryFn: () =>
      fileControllerGetFile({
        path: {
          fileUuid: fileUuid!,
        },
      }).then((res) => res.data),
  });

  const { data: progress } = useQuery({
    enabled: ready,
    queryKey: queryKeys.fileChild({
      fileUuid: fileUuid!,
      childName: "progress",
    }),
    queryFn: async () => {
      const response = await fileControllerGetParsingProgress({
        path: {
          fileUuid: fileUuid!,
        },
      });

      return response.data;
    },
    // Polls while isParsing is true
    refetchInterval: (data) => {
      const isParsing = data.state.data?.isParsing;
      return isParsing ? POLLING_INTERVAL : false;
    },
  });

  useEffect(() => {
    if (progress?.isParsing === false) {
      refetch();
    }
  }, [progress?.isParsing]);

  const { data: markdown } = useQuery({
    enabled:
      ready &&
      !!progress &&
      progress.currentStep > progress.stepNames.indexOf("markdown_and_nodes"),
    queryKey: queryKeys.fileChild({
      fileUuid: fileUuid!,
      childName: "markdown",
    }),
    queryFn: async () => {
      const response = await fileControllerGetMarkdown({
        path: {
          fileUuid: fileUuid!,
        },
      });
      return response.data;
    },
  });

  const { data: ragSummary } = useQuery({
    enabled: ready && progress?.isParsing === false,
    queryKey: queryKeys.fileChild({
      fileUuid: fileUuid!,
      childName: "rag-summary",
    }),
    queryFn: async () => {
      const response = await fileControllerGetRagSummary({
        path: {
          fileUuid: fileUuid!,
        },
      });
      return response.data;
    },
  });

  const { mutateAsync: reparse } = useMutation({
    mutationFn: async () => {
      if (!fileUuid) {
        throw new Error("File UUID is required");
      }

      await fileControllerParseFile({
        path: {
          fileUuid,
        },
      });
    },
  });

  return {
    file,
    progress,
    markdown,
    ragSummary: ragSummary || [],
    reparse,
  };
};
